import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, 
  Building2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  FileText, 
  RefreshCw,
  DollarSign,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

export default function InspectionPage() {
  const { t } = useTranslation();
  const [inspection, setInspection] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [costs, setCosts] = useState({ costs: [], buyer_total: 0, seller_total: 0 });
  
  // Re-test form
  const [retestReason, setRetestReason] = useState('');
  const [requestingRetest, setRequestingRetest] = useState(false);

  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const transactionId = searchParams.get('transaction');

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/warehouses`);
      setWarehouses(response.data.warehouses);
    } catch (error) {
      console.error('Failed to fetch warehouses');
    }
  }, []);

  const fetchInspection = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/inspections/transaction/${transactionId}`,
        { headers: getAuthHeader() }
      );
      setInspection(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch inspection');
      }
    } finally {
      setLoading(false);
    }
  }, [transactionId, getAuthHeader]);

  const fetchCosts = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/costs/transaction/${transactionId}`,
        { headers: getAuthHeader() }
      );
      setCosts(response.data);
    } catch (error) {
      console.error('Failed to fetch costs');
    }
  }, [transactionId, getAuthHeader]);

  useEffect(() => {
    fetchWarehouses();
    if (transactionId) {
      fetchInspection();
      fetchCosts();
    }
  }, [transactionId, fetchWarehouses, fetchInspection, fetchCosts]);

  const handleScheduleInspection = async () => {
    if (!selectedWarehouse) {
      toast.error('Please select a warehouse');
      return;
    }

    setScheduling(true);
    try {
      const response = await axios.post(
        `${API_URL}/inspections/schedule?transaction_id=${transactionId}&warehouse_id=${selectedWarehouse}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Inspection scheduled successfully!');
      setInspection({
        ...response.data,
        status: 'scheduled',
        hours_remaining: 72
      });
      fetchInspection();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule inspection');
    } finally {
      setScheduling(false);
    }
  };

  const handleRequestRetest = async () => {
    if (!retestReason.trim()) {
      toast.error('Please provide a reason for re-testing');
      return;
    }

    setRequestingRetest(true);
    try {
      const formData = new FormData();
      formData.append('reason', retestReason);

      await axios.post(
        `${API_URL}/inspections/${inspection.id}/request-retest`,
        formData,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Re-test requested successfully');
      fetchInspection();
      fetchCosts();
      setRetestReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request re-test');
    } finally {
      setRequestingRetest(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { color: 'bg-[#3B82F6]/10 text-[#3B82F6]', icon: Clock, label: 'Scheduled' },
      in_progress: { color: 'bg-[#F59E0B]/10 text-[#F59E0B]', icon: RefreshCw, label: 'In Progress' },
      completed: { color: 'bg-[#10B981]/10 text-[#10B981]', icon: CheckCircle, label: 'Completed' },
      retest_pending: { color: 'bg-[#F59E0B]/10 text-[#F59E0B]', icon: RefreshCw, label: 'Re-test Pending' },
      expired: { color: 'bg-[#EF4444]/10 text-[#EF4444]', icon: XCircle, label: 'Expired' },
      cancelled: { color: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.scheduled;
    const Icon = config.icon;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </button>

        <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-[#F59E0B]" />
            {t('inspection.title')}
          </h1>
          <p className="text-[#94A3B8] mb-8">
            {t('inspection.subtitle')}
          </p>

          {!inspection ? (
            /* Schedule New Inspection */
            <div className="border-t border-[#1E293B] pt-8">
              <h2 className="text-xl font-semibold text-white mb-4">{t('inspection.scheduleTitle')}</h2>
              <p className="text-[#94A3B8] mb-6">
                Select an approved warehouse for mineral inspection. You have <strong>72 hours</strong> to complete the inspection after scheduling.
              </p>

              <div className="space-y-6">
                <div>
                  <Label className="text-white font-medium">Select Inspection Warehouse *</Label>
                  <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Choose an approved warehouse..." />
                    </SelectTrigger>
                    <SelectContent>
                      {warehouses.map((warehouse) => (
                        <SelectItem key={warehouse.id} value={warehouse.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-[#64748B]" />
                            <span>{warehouse.name}</span>
                            <span className="text-[#64748B]">- {warehouse.location}, {warehouse.country}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedWarehouse && (
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                    <p className="text-[#3B82F6] text-sm">
                      <strong>Note:</strong> Once scheduled, you must complete the inspection within 72 hours. 
                      The mineral will be delivered to the selected warehouse for quality assessment and assay testing.
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleScheduleInspection}
                  disabled={scheduling || !selectedWarehouse}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                >
                  {scheduling ? 'Scheduling...' : 'Schedule Inspection'}
                </Button>
              </div>
            </div>
          ) : (
            /* Inspection Details */
            <div className="space-y-8">
              {/* Status & Deadline */}
              <div className="flex items-center justify-between flex-wrap gap-4 pb-6 border-b border-[#1E293B]">
                {getStatusBadge(inspection.status)}
                
                {inspection.hours_remaining !== undefined && inspection.status === 'scheduled' && (
                  <div className={`flex items-center gap-2 ${inspection.hours_remaining < 12 ? 'text-[#EF4444]' : 'text-[#94A3B8]'}`}>
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">
                      {inspection.hours_remaining.toFixed(1)} hours remaining
                    </span>
                  </div>
                )}
              </div>

              {/* Warehouse Info */}
              <div className="bg-[#0B1120] p-6 rounded-xl border border-[#1E293B]">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#F59E0B]" />
                  Inspection Location
                </h3>
                <p className="text-lg font-medium text-white">{inspection.warehouse_name}</p>
                <p className="text-[#94A3B8]">{inspection.warehouse_location}</p>
              </div>

              {/* Preliminary Assay Results */}
              {inspection.preliminary_assay_result && (
                <div className={`p-6 rounded-xl border ${
                  inspection.preliminary_meets_spec 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Preliminary Assay Results
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#64748B]">Grade</p>
                      <p className="text-lg font-semibold text-white">{inspection.preliminary_grade}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Purity</p>
                      <p className="text-lg font-semibold text-white">{inspection.preliminary_purity}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Inspector</p>
                      <p className="text-white">{inspection.preliminary_inspector}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Status</p>
                      <p className={`font-semibold ${inspection.preliminary_meets_spec ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {inspection.preliminary_meets_spec ? '✓ Meets Specification' : '✗ Does Not Meet Specification'}
                      </p>
                    </div>
                  </div>

                  {inspection.inspector_notes && (
                    <div className="mt-4 pt-4 border-t border-[#1E293B]">
                      <p className="text-sm text-[#64748B] mb-1">Inspector Notes</p>
                      <p className="text-[#94A3B8]">{inspection.inspector_notes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Re-test Request (for seller when quality fails) */}
              {inspection.preliminary_assay_result && 
               !inspection.preliminary_meets_spec && 
               !inspection.retest_requested &&
               user?.id === inspection.seller_id && (
                <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-orange-600" />
                    Request Re-Testing
                  </h3>
                  <p className="text-[#94A3B8] mb-4">
                    If you dispute the preliminary results, you can request an independent re-test. 
                    <strong className="text-[#F59E0B]"> Cost: $500 (paid by seller)</strong>
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white">Reason for Re-Test Request *</Label>
                      <Textarea
                        value={retestReason}
                        onChange={(e) => setRetestReason(e.target.value)}
                        placeholder="Explain why you believe the preliminary results are inaccurate..."
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                    <Button
                      onClick={handleRequestRetest}
                      disabled={requestingRetest}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      {requestingRetest ? 'Requesting...' : 'Request Re-Test ($500)'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Re-test Status */}
              {inspection.retest_requested && (
                <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                  <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 text-yellow-600" />
                    Re-Test Requested
                  </h3>
                  <p className="text-[#94A3B8] mb-2">Reason: {inspection.retest_reason}</p>
                  <p className="text-[#94A3B8]">Cost: ${inspection.retest_cost} (Seller)</p>
                  
                  {inspection.final_assay_result && (
                    <div className="mt-4 pt-4 border-t border-yellow-200">
                      <h4 className="font-medium text-white mb-2">Final Assay Results</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-[#64748B]">Grade</p>
                          <p className="font-semibold">{inspection.final_grade}</p>
                        </div>
                        <div>
                          <p className="text-sm text-[#64748B]">Purity</p>
                          <p className="font-semibold">{inspection.final_purity}%</p>
                        </div>
                        <div>
                          <p className="text-sm text-[#64748B]">Status</p>
                          <p className={`font-semibold ${inspection.final_meets_spec ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                            {inspection.final_meets_spec ? '✓ Meets Specification' : '✗ Does Not Meet Specification'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cost Allocation */}
              {costs.costs.length > 0 && (
                <div className="border-t border-[#1E293B] pt-8">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#10B981]" />
                    Cost Breakdown
                  </h3>
                  
                  <div className="space-y-3">
                    {costs.costs.map((cost) => (
                      <div 
                        key={cost.id}
                        className="flex items-center justify-between p-3 bg-[#0B1120] rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-white capitalize">{cost.cost_type.replace('_', ' ')}</p>
                          <p className="text-sm text-[#64748B]">{cost.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">${cost.amount.toFixed(2)}</p>
                          <p className="text-sm text-[#64748B]">Paid by {cost.paid_by}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#1E293B] flex justify-between">
                    <div>
                      <p className="text-sm text-[#64748B]">Buyer Total</p>
                      <p className="font-semibold text-white">${costs.buyer_total.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Seller Total</p>
                      <p className="font-semibold text-white">${costs.seller_total.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#64748B]">Grand Total</p>
                      <p className="font-bold text-lg text-white">${costs.grand_total.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
